import { trackProductInfoProductpagevideodetailsView } from "@faire/web-api/events/productInfo/view/productpagevideodetails";

import { AppHistory } from "@faire/retailer/packages/core/services/AppHistory";

import { PreloadedImageTracker } from "./PreloadedImageTracker";

export function trackPreloadedImageGrid(
  imageGridElement: HTMLElement,
  pdpShowVerticalImagery: boolean,
  pdpBrandIsApparel: boolean
) {
  if (AppHistory.get().history.action === "POP") {
    // Don't track PDP navigations triggered by back or forward.
    // There is be no opportunity for preloading in this case,
    // and images are likely still in the cache from the previous visit anyway.
    return;
  }

  const previousPath = AppHistory.get().previousLocation()?.pathname ?? "";

  const imgElements = Array.from(imageGridElement.querySelectorAll("img"));
  Promise.all(imgElements.map(waitForImageLoad)).then((imgs) => {
    const { summaryString, counts, differences } = comparePreloadedUrls(
      imgs.map((img) => img.currentSrc)
    );

    trackProductInfoProductpagevideodetailsView(
      "V5_" + summaryString,
      JSON.stringify({
        ...counts,
        pdpShowVerticalImagery,
        pdpBrandIsApparel,
      }),
      [previousPath, ...differences].join("|")
    );
  });
}

export function comparePreloadedUrls(urls: string[]) {
  const results = urls.map((url) => findPreloadedUrl(url));

  const counts = {
    preloadDiffers: 0,
    noPreload: 0,
    success: 0,
    showVerticalImagery: 0,
  };
  const differencesSet = new Set<string>();
  results.forEach((result) => {
    if (result.type === "NO_PRELOAD") {
      counts.noPreload += 1;
    } else {
      if (result.type === "SUCCESS") {
        counts.success += 1;
      }

      if (result.type === "PRELOAD_DIFFERS") {
        counts.preloadDiffers += 1;
        differencesSet.add(result.differences);
      }

      if (result.showVerticalImagery) {
        counts.showVerticalImagery += 1;
      }
    }
  });

  return {
    summaryString: getSummaryString(results),
    counts,
    differences: Array.from(differencesSet),
  };
}

function waitForImageLoad(img: HTMLImageElement): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    function onLoadOrError() {
      img.removeEventListener("load", onLoadOrError);
      img.removeEventListener("error", onLoadOrError);
      resolve(img);
    }
    img.addEventListener("load", onLoadOrError);
    img.addEventListener("error", onLoadOrError);
  });
}

type PreloadMatch =
  | { type: "NO_PRELOAD" }
  | {
      type: "SUCCESS" | "PRELOAD_DIFFERS";
      differences: string;
      showVerticalImagery: boolean;
    };

function findPreloadedUrl(currentSrc: string): PreloadMatch {
  const match = PreloadedImageTracker.get().getInfo(currentSrc);
  if (match == null) {
    return { type: "NO_PRELOAD" };
  }

  const differences =
    currentSrc !== match.img.currentSrc
      ? JSON.stringify(diffUrls(match.img.currentSrc, currentSrc))
      : "";
  return {
    type: differences ? "PRELOAD_DIFFERS" : "SUCCESS",
    differences,
    showVerticalImagery: match.showVerticalImagery,
  };
}

function diffUrls(
  urlStrA: string,
  urlStrB: string
): [string | undefined, string | undefined][] {
  if (urlStrA === urlStrB) {
    return [];
  }

  let urlA: URL;
  let urlB: URL;
  try {
    urlA = new URL(urlStrA);
    urlB = new URL(urlStrB);
  } catch {
    return [["invalid URL", undefined]];
  }

  const mapFromQueryString = (search: string) => {
    return new Map(search.split("&").map((str) => [str.split("=", 1)[0], str]));
  };

  const mapA = mapFromQueryString(urlA.search);
  const mapB = mapFromQueryString(urlB.search);
  const keySet = new Set([...mapA.keys(), ...mapB.keys()]);

  const diffPairs: [string | undefined, string | undefined][] = [
    [urlA.origin, urlB.origin],
    [urlA.pathname, urlB.pathname],
  ];
  keySet.forEach((key) => {
    diffPairs.push([mapA.get(key), mapB.get(key)]);
  });

  return diffPairs.filter(([a, b]) => a !== b);
}

function getSummaryString(results: PreloadMatch[]): string {
  const types = new Set(results.map((r) => r.type));
  if (types.size === 0) {
    return "ZERO_COUNT";
  } else if (types.size === 1 && types.has("SUCCESS")) {
    return "ALL_SUCCESS";
  } else if (types.size === 1 && types.has("NO_PRELOAD")) {
    return "ALL_NO_PRELOAD";
  } else if (
    types.size === 2 &&
    types.has("NO_PRELOAD") &&
    types.has("SUCCESS")
  ) {
    return "ALL_SUCCESS_OR_NO_PRELOAD";
  } else if (types.has("PRELOAD_DIFFERS")) {
    return "SOME_DIFFER";
  } else {
    return "UNKNOWN";
  }
}
